import { SESSIONS, WEEKMAP } from "../data/sessions";
import { periodDay } from "./cycle";
import { careWalkDay, withCareWalk } from "./cycleCare";
import { DAY_MS, parseDay, todayStr } from "./date";
import type { Block, BlockItem, KayaState, PhaseIndex, Session, SessionKey } from "./types";
import { isTwoSided } from "./exerciseMeta";
import { fillTemplate, programmeFor, steadyWeekFor, type FillOptions } from "./programme";
import { doseStepFor } from "./readiness";
import { isSteadyKey } from "./templates";

export const TYPENAME: Record<SessionKey, string> = {
  A: "Strength circuit",
  B: "Fat-burn intervals",
  C: "Core & mobility",
  T: "Travel floor",
  R: "Rest",
  P: "Gentle period session",
  Y: "Cycle care",
  SB: "Strength & balance",
  GC: "Gentle cardio & mobility",
  CH: "Chair session",
  MJ: "Morning joints",
  WK: "Walk day",
  YB: "Morning yoga & breathing",
};

/**
 * Metabolic equivalent per session type, used for the calorie estimate. The Steady templates' are
 * the Older Adult Compendium's MET_60+ values multiplied by 0.77: the Compendium assumes 2.7 ml/kg
 * a minute per MET, Kaya's `MET × kg × hours` assumes 3.5, and unconverted they overstate a 60+
 * session by about 30 % (move-library-older-adults.md §3). Strength & balance and Gentle cardio
 * 3.8 → 2.9; the Chair session 2.4; Morning joints is the adult stretching value, 2.3, unconverted;
 * Walk day takes the walk's own, the slowest band's 4.0 → 3.1 — the walk screen still prices a
 * logged walk by its measured speed (`walkMet`). (ADR-052)
 */
export const MET: Record<SessionKey, number> = {
  A: 3.8,
  B: 5.5,
  C: 2.8,
  T: 3.8,
  R: 0,
  P: 2.3,
  Y: 2.3,
  SB: 2.9,
  GC: 2.9,
  CH: 2.4,
  MJ: 2.3,
  WK: 3.1,
  // as Cycle care's: stretching and slow breath (ADR-067)
  YB: 2.3,
};

/** Weeks since the profile start date, 1-based. */
export function weekNo(S: KayaState): number {
  const s = parseDay(S.profile.startDate);
  const d = Math.floor((Date.now() - s.getTime()) / DAY_MS);
  return Math.max(1, Math.floor(d / 7) + 1);
}

/** Difficulty phase: weeks 1–2 → 0, weeks 3–4 → 1, week 5+ → 2. Manually overridable. The
 *  calendar never moves a Steady person (plan §3.4: "never at 60+"): their doses hold at the
 *  beginner's until feedback or a pinned phase moves them. (ADR-052) */
export function phase(S: KayaState): PhaseIndex {
  if (S.profile.phaseMode !== "auto") return +S.profile.phaseMode as PhaseIndex;
  if (S.profile.training && programmeFor(S) === "steady") return 0;
  const w = weekNo(S);
  return (w <= 2 ? 0 : w <= 4 ? 1 : 2) as PhaseIndex;
}

/**
 * The dose index a session runs at: the calendar phase for the Kaya programme; for a Steady template
 * the step How was it? has reached (`readiness.ts::doseStepFor`), because the calendar never moves a
 * Steady person (ADR-052) and feedback does (ADR-054). Everything that builds a queue to run, list,
 * time or freeze passes this, never `phase(S)` alone.
 */
export function doseIndex(S: KayaState, key: SessionKey | string, ds = todayStr()): PhaseIndex {
  return isSteadyKey(key) ? doseStepFor(S, key, ds) : phase(S);
}

/** Hip-safe mode drops the squat/lunge variants from every block. */
export function items(S: KayaState, b: Block): BlockItem[] {
  return b.items.filter((it) => !(it.hipfree && S.profile.hipSafe));
}

/**
 * The weekday map this person follows, Sunday first: `profile.training.week` when it names seven
 * days, the Steady week worked out from the answers for someone on the Steady programme
 * (ADR-052), today's `WEEKMAP` otherwise. Everything that asks "what is scheduled" or "is this a
 * rest day" reads it — the day's session, streaks, freezes, missed days and "Clean week" — so a
 * rest day in a three-day week is a rest day and not a missed one. (ADR-050)
 */
export function weekMapFor(S: KayaState): readonly SessionKey[] {
  const w = S.profile?.training?.week;
  if (Array.isArray(w) && w.length === 7 && w.every((k) => typeof k === "string" && k.length > 0)) return w;
  return (S.profile?.training && steadyWeekFor(S)) || WEEKMAP;
}

/** Sessions in the person's week: six in the default map. */
export function sessionsPerWeek(S: KayaState): number {
  return weekMapFor(S).filter((k) => k !== "R").length;
}

/** Which session `date` is scheduled for: explicit override, then travel, then the weekday
 *  map, with period days swapping a working day for the gentle session. */
export function sessionKeyFor(S: KayaState, date?: string): SessionKey {
  const ds = date || todayStr();
  if (S.overrides[ds]) return S.overrides[ds];
  // a parent's travel day is the chair session: a hotel room and a relative's home have a chair
  if (S.travelDates[ds]) return S.profile.training && programmeFor(S, ds) === "steady" ? "CH" : "T";
  const w = weekMapFor(S)[parseDay(ds).getDay()];
  if (w !== "R" && periodDay(S, ds)) return "P";
  return w;
}

/**
 * A day before the plan began. It was never scheduled, so it can be neither missed, rescued nor
 * frozen: a brand-new account used to open on "Missed Tuesday" and spend its banked freeze on a
 * day it had not yet signed up for. The reference app has the same bug.
 */
export function beforePlan(S: KayaState, ds: string): boolean {
  const start = S.profile.startDate;
  return !!start && ds < start.slice(0, 10);
}

/** A day the streak is allowed to skip: a scheduled rest day, or a period day. */
export function neutralDay(S: KayaState, ds: string): boolean {
  return weekMapFor(S)[parseDay(ds).getDay()] === "R" || periodDay(S, ds);
}

export function doseText(it: BlockItem, p: PhaseIndex): string {
  // Same union the player uses, so the plan listing never promises a dose the session does not
  // run — a corrected one-sided move reads "30 s / side" on both screens or on neither.
  const side = isTwoSided(it.id, it.side);
  if (it.t) return it.t[p] + " s" + (side ? " / side" : "");
  return it.r![p] + (side ? " / side" : "");
}

/** A key this build has a session for: the reference's, or a Steady template (ADR-052). */
export function isSessionKey(key: unknown): key is SessionKey {
  return (typeof key === "string" && Object.prototype.hasOwnProperty.call(SESSIONS, key)) || isSteadyKey(key);
}

/**
 * The session for a key, and the travel session for a key this build does not know. A later
 * build adds keys, and a phone still on an older cached build meets them in `overrides`,
 * `sessionKey` or a person's week — `SESSIONS[key].blocks` then threw. Every read of a session
 * goes through here; the server compiles the same module. (ADR-050)
 */
export function sessionFor(key: SessionKey | string, S?: KayaState | null, opts?: FillOptions): Session {
  // A Steady template is filled for the person (ADR-052); without one, as written.
  if (isSteadyKey(key)) return fillTemplate(S ?? null, key, opts);
  // Cycle care gains a walk on a premenstrual day (ADR-057) — added here, never in the data.
  if (key === "Y" && S && careWalkDay(S, opts?.ds ?? todayStr())) return withCareWalk(SESSIONS.Y);
  return Object.prototype.hasOwnProperty.call(SESSIONS, key) ? SESSIONS[key as keyof typeof SESSIONS] : SESSIONS.T;
}

/** The session's short name, through `sessionFor` so an unknown key reads as travel. */
export function typeName(key: SessionKey | string): string {
  return TYPENAME[sessionFor(key).type];
}

/** The session's MET, through `sessionFor` for the same reason. */
export function metFor(key: SessionKey | string): number {
  return MET[sessionFor(key).type];
}
